import {
  Scene,
  MeshBuilder,
  Vector3,
  Color3,
  StandardMaterial,
  Mesh,
  Animation,
  ParticleSystem,
  Texture,
  Color4,
} from '@babylonjs/core';

export enum MachineType {
  // Tier 1 - Basic
  CANDY_MIXER = 'candy_mixer',
  LOLLIPOP_MAKER = 'lollipop_maker',
  CHOCOLATE_FOUNTAIN = 'chocolate_fountain',

  // Tier 2 - Advanced
  GUMMY_PRESS = 'gummy_press',
  COOKIE_OVEN = 'cookie_oven',
  ICE_CREAM_MACHINE = 'ice_cream_machine',

  // Tier 3 - Premium
  CAKE_FACTORY = 'cake_factory',
  DONUT_FRYER = 'donut_fryer',
  RAINBOW_GENERATOR = 'rainbow_generator',
}

export interface MachineConfig {
  type: MachineType;
  name: string;
  description: string;
  cost: number;
  income: number; // Per second
  color: Color3;
  accentColor: Color3;
  icon: string;
  tier: number;
  unlockAt: number; // Total earnings needed to unlock
}

export const MACHINE_CONFIGS: Record<MachineType, MachineConfig> = {
  [MachineType.CANDY_MIXER]: {
    type: MachineType.CANDY_MIXER,
    name: 'Misturador de Doces',
    description: 'Mistura ingredientes para fazer doces coloridos',
    cost: 50,
    income: 2,
    color: new Color3(1, 0.4, 0.7), // Pink
    accentColor: new Color3(1, 0.6, 0.8),
    icon: '🍬',
    tier: 1,
    unlockAt: 0,
  },
  [MachineType.LOLLIPOP_MAKER]: {
    type: MachineType.LOLLIPOP_MAKER,
    name: 'Fabricadora de Pirulitos',
    description: 'Cria pirulitos deliciosos de vários sabores',
    cost: 150,
    income: 5,
    color: new Color3(0.9, 0.2, 0.2), // Red
    accentColor: new Color3(1, 0.4, 0.4),
    icon: '🍭',
    tier: 1,
    unlockAt: 100,
  },
  [MachineType.CHOCOLATE_FOUNTAIN]: {
    type: MachineType.CHOCOLATE_FOUNTAIN,
    name: 'Fonte de Chocolate',
    description: 'Uma fonte mágica de chocolate derretido',
    cost: 400,
    income: 12,
    color: new Color3(0.4, 0.2, 0.1), // Brown
    accentColor: new Color3(0.6, 0.3, 0.15),
    icon: '🍫',
    tier: 1,
    unlockAt: 300,
  },
  [MachineType.GUMMY_PRESS]: {
    type: MachineType.GUMMY_PRESS,
    name: 'Prensa de Gomas',
    description: 'Produz gomas macias em formatos divertidos',
    cost: 1000,
    income: 28,
    color: new Color3(0.2, 0.8, 0.4), // Green
    accentColor: new Color3(0.3, 0.9, 0.5),
    icon: '🐻',
    tier: 2,
    unlockAt: 800,
  },
  [MachineType.COOKIE_OVEN]: {
    type: MachineType.COOKIE_OVEN,
    name: 'Forno de Cookies',
    description: 'Assa cookies crocantes e quentinhos',
    cost: 2500,
    income: 65,
    color: new Color3(0.85, 0.65, 0.4), // Cookie color
    accentColor: new Color3(0.95, 0.75, 0.5),
    icon: '🍪',
    tier: 2,
    unlockAt: 2000,
  },
  [MachineType.ICE_CREAM_MACHINE]: {
    type: MachineType.ICE_CREAM_MACHINE,
    name: 'Máquina de Sorvete',
    description: 'Produz sorvetes cremosos de vários sabores',
    cost: 6000,
    income: 140,
    color: new Color3(0.7, 0.9, 1), // Light blue
    accentColor: new Color3(0.8, 0.95, 1),
    icon: '🍦',
    tier: 2,
    unlockAt: 5000,
  },
  [MachineType.CAKE_FACTORY]: {
    type: MachineType.CAKE_FACTORY,
    name: 'Fábrica de Bolos',
    description: 'Produz bolos elaborados automaticamente',
    cost: 15000,
    income: 320,
    color: new Color3(1, 0.85, 0.9), // Light pink
    accentColor: new Color3(1, 0.95, 0.95),
    icon: '🎂',
    tier: 3,
    unlockAt: 12000,
  },
  [MachineType.DONUT_FRYER]: {
    type: MachineType.DONUT_FRYER,
    name: 'Fritadeira de Donuts',
    description: 'Frita donuts perfeitos com coberturas',
    cost: 40000,
    income: 750,
    color: new Color3(1, 0.6, 0.2), // Orange
    accentColor: new Color3(1, 0.75, 0.4),
    icon: '🍩',
    tier: 3,
    unlockAt: 30000,
  },
  [MachineType.RAINBOW_GENERATOR]: {
    type: MachineType.RAINBOW_GENERATOR,
    name: 'Gerador Arco-Íris',
    description: 'A máquina suprema! Gera doces mágicos',
    cost: 100000,
    income: 2000,
    color: new Color3(0.8, 0.4, 1), // Purple
    accentColor: new Color3(0.9, 0.6, 1),
    icon: '🌈',
    tier: 3,
    unlockAt: 80000,
  },
};

export interface PlacedMachine {
  id: string;
  type: MachineType;
  mesh: Mesh;
  position: Vector3;
  level: number;
  particles?: ParticleSystem;
}

export class TycoonObjects {
  private scene: Scene;
  private machines: Map<string, PlacedMachine> = new Map();
  private machineIdCounter: number = 0;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  public createGround(): Mesh {
    // Main factory floor
    const ground = MeshBuilder.CreateGround(
      'factory-floor',
      { width: 50, height: 50, subdivisions: 4 },
      this.scene
    );

    const groundMat = new StandardMaterial('ground-mat', this.scene);

    // Load wood floor texture
    const woodTexture = new Texture('/assets/textures/tycoon/WoodFloor048_1K-JPG_Color.jpg', this.scene);
    woodTexture.uScale = 12; // Tile the texture
    woodTexture.vScale = 12;
    groundMat.diffuseTexture = woodTexture;
    groundMat.specularColor = new Color3(0.15, 0.12, 0.1);
    groundMat.specularPower = 32;
    ground.material = groundMat;

    // Create checkered tile area near entrance
    this.createCheckeredEntrance();

    // Create complete factory structure
    this.createFactoryStructure();

    // Create shelves with candy products
    this.createCandyShelves();

    // Create counter/reception area
    this.createReceptionArea();

    // Create NPC vendor
    this.createVendorNPC();

    // Create decorative elements
    this.createDecorations();

    // Create hanging decorations
    this.createHangingDecorations();

    // Create candy displays
    this.createCandyDisplays();

    // Create glass display vitrines near entrance
    this.createGlassVitrines();

    return ground;
  }

  private createCheckeredEntrance(): void {
    // Create a checkered pink/white tile area near the entrance for a candy shop feel
    const tileSize = 1.5;
    const tilesX = 10;
    const tilesZ = 6;
    const startZ = 15; // Near entrance

    // Pink tile material with texture
    const pinkTileMat = new StandardMaterial('pink-tile-mat', this.scene);
    const pinkTexture = new Texture('/assets/textures/tycoon/Tiles013_1K-JPG_Color.jpg', this.scene);
    pinkTexture.uScale = 1;
    pinkTexture.vScale = 1;
    pinkTileMat.diffuseTexture = pinkTexture;
    pinkTileMat.diffuseColor = new Color3(1, 0.75, 0.85); // Pink tint
    pinkTileMat.specularColor = new Color3(0.4, 0.4, 0.4);
    pinkTileMat.specularPower = 64;

    // White tile material with texture
    const whiteTileMat = new StandardMaterial('white-tile-mat', this.scene);
    const whiteTexture = new Texture('/assets/textures/tycoon/Tiles013_1K-JPG_Color.jpg', this.scene);
    whiteTexture.uScale = 1;
    whiteTexture.vScale = 1;
    whiteTileMat.diffuseTexture = whiteTexture;
    whiteTileMat.diffuseColor = new Color3(1, 0.98, 0.98); // White tint
    whiteTileMat.specularColor = new Color3(0.5, 0.5, 0.5);
    whiteTileMat.specularPower = 64;

    for (let x = 0; x < tilesX; x++) {
      for (let z = 0; z < tilesZ; z++) {
        const isLight = (x + z) % 2 === 0;
        const tile = MeshBuilder.CreateBox(
          `entrance-tile-${x}-${z}`,
          { width: tileSize - 0.02, height: 0.08, depth: tileSize - 0.02 },
          this.scene
        );

        tile.position = new Vector3(
          (x - tilesX / 2) * tileSize + tileSize / 2,
          0.04,
          startZ + (z - tilesZ / 2) * tileSize + tileSize / 2
        );

        tile.material = isLight ? whiteTileMat : pinkTileMat;
      }
    }
  }

  private createFactoryStructure(): void {
    const wallHeight = 8;
    const wallLength = 50;
    const wallThickness = 0.5;

    // Main wall material - pink/cream candy theme with texture
    const wallMat = new StandardMaterial('wall-mat', this.scene);
    wallMat.diffuseColor = new Color3(1, 0.92, 0.93);
    wallMat.specularColor = new Color3(0.1, 0.1, 0.1);
    const wallTexture = new Texture('/assets/textures/tycoon/PaintedPlaster017_1K-JPG_Color.jpg', this.scene);
    wallTexture.uScale = 8;
    wallTexture.vScale = 4;
    wallMat.diffuseTexture = wallTexture;

    // Back wall
    const backWall = MeshBuilder.CreateBox(
      'back-wall',
      { width: wallLength, height: wallHeight, depth: wallThickness },
      this.scene
    );
    backWall.position = new Vector3(0, wallHeight / 2, -wallLength / 2);
    backWall.material = wallMat;

    // Side walls
    const leftWall = MeshBuilder.CreateBox(
      'left-wall',
      { width: wallThickness, height: wallHeight, depth: wallLength },
      this.scene
    );
    leftWall.position = new Vector3(-wallLength / 2, wallHeight / 2, 0);
    leftWall.material = wallMat;

    const rightWall = MeshBuilder.CreateBox(
      'right-wall',
      { width: wallThickness, height: wallHeight, depth: wallLength },
      this.scene
    );
    rightWall.position = new Vector3(wallLength / 2, wallHeight / 2, 0);
    rightWall.material = wallMat;

    // Front wall with entrance gap
    const frontWallLeft = MeshBuilder.CreateBox(
      'front-wall-left',
      { width: 18, height: wallHeight, depth: wallThickness },
      this.scene
    );
    frontWallLeft.position = new Vector3(-16, wallHeight / 2, wallLength / 2);
    frontWallLeft.material = wallMat;

    const frontWallRight = MeshBuilder.CreateBox(
      'front-wall-right',
      { width: 18, height: wallHeight, depth: wallThickness },
      this.scene
    );
    frontWallRight.position = new Vector3(16, wallHeight / 2, wallLength / 2);
    frontWallRight.material = wallMat;

    // Door frame
    const doorFrameMat = new StandardMaterial('door-frame-mat', this.scene);
    doorFrameMat.diffuseColor = new Color3(0.6, 0.3, 0.15);
    doorFrameMat.specularColor = new Color3(0.3, 0.2, 0.1);

    const doorTop = MeshBuilder.CreateBox(
      'door-top',
      { width: 14, height: 1, depth: 0.8 },
      this.scene
    );
    doorTop.position = new Vector3(0, wallHeight - 0.5, wallLength / 2);
    doorTop.material = doorFrameMat;

    // Candy cane stripes on walls (red and white alternating)
    const candyCaneRed = new StandardMaterial('candy-red-mat', this.scene);
    candyCaneRed.diffuseColor = new Color3(0.9, 0.1, 0.15);
    candyCaneRed.specularColor = new Color3(0.4, 0.2, 0.2);
    candyCaneRed.emissiveColor = new Color3(0.15, 0.02, 0.02);

    const candyCaneWhite = new StandardMaterial('candy-white-mat', this.scene);
    candyCaneWhite.diffuseColor = new Color3(1, 0.98, 0.95);
    candyCaneWhite.specularColor = new Color3(0.3, 0.3, 0.3);

    const stripeHeight = 0.6;
    const numStripes = 10;

    // Back wall candy cane stripes
    for (let i = 0; i < numStripes; i++) {
      const stripe = MeshBuilder.CreateBox(
        `back-stripe-${i}`,
        { width: wallLength, height: stripeHeight, depth: 0.05 },
        this.scene
      );
      stripe.position = new Vector3(0, 0.8 + i * stripeHeight, -wallLength / 2 + 0.3);
      stripe.material = i % 2 === 0 ? candyCaneRed : candyCaneWhite;
    }

    // Left wall candy cane stripes
    for (let i = 0; i < numStripes; i++) {
      const stripe = MeshBuilder.CreateBox(
        `left-stripe-${i}`,
        { width: 0.05, height: stripeHeight, depth: wallLength },
        this.scene
      );
      stripe.position = new Vector3(-wallLength / 2 + 0.3, 0.8 + i * stripeHeight, 0);
      stripe.material = i % 2 === 0 ? candyCaneRed : candyCaneWhite;
    }

    // Right wall candy cane stripes
    for (let i = 0; i < numStripes; i++) {
      const stripe = MeshBuilder.CreateBox(
        `right-stripe-${i}`,
        { width: 0.05, height: stripeHeight, depth: wallLength },
        this.scene
      );
      stripe.position = new Vector3(wallLength / 2 - 0.3, 0.8 + i * stripeHeight, 0);
      stripe.material = i % 2 === 0 ? candyCaneRed : candyCaneWhite;
    }

    // Roof/ceiling - solid with candy theme
    const roofMat = new StandardMaterial('roof-mat', this.scene);
    roofMat.diffuseColor = new Color3(1, 0.85, 0.9); // Pink candy color
    roofMat.specularColor = new Color3(0.2, 0.2, 0.2);

    const roof = MeshBuilder.CreateBox(
      'roof',
      { width: wallLength, height: 0.5, depth: wallLength },
      this.scene
    );
    roof.position = new Vector3(0, wallHeight, 0);
    roof.material = roofMat;

    // Inner ceiling (visible from inside)
    const ceilingMat = new StandardMaterial('ceiling-mat', this.scene);
    ceilingMat.diffuseColor = new Color3(1, 0.95, 0.97); // Light cream
    ceilingMat.specularColor = new Color3(0.1, 0.1, 0.1);

    const ceiling = MeshBuilder.CreateBox(
      'ceiling',
      { width: wallLength - 1, height: 0.2, depth: wallLength - 1 },
      this.scene
    );
    ceiling.position = new Vector3(0, wallHeight - 0.4, 0);
    ceiling.material = ceilingMat;

    // Roof beams
    const beamMat = new StandardMaterial('beam-mat', this.scene);
    beamMat.diffuseColor = new Color3(0.9, 0.7, 0.8);

    for (let i = -2; i <= 2; i++) {
      const beam = MeshBuilder.CreateBox(
        `roof-beam-${i}`,
        { width: wallLength, height: 0.5, depth: 0.5 },
        this.scene
      );
      beam.position = new Vector3(0, wallHeight - 0.5, i * 10);
      beam.material = beamMat;
    }

    // Windows on side walls
    this.createWindows(wallLength, wallHeight);

    // Factory sign above entrance
    this.createFactorySign();
  }

  private createWindows(wallLength: number, wallHeight: number): void {
    const windowMat = new StandardMaterial('window-mat', this.scene);
    windowMat.diffuseColor = new Color3(0.7, 0.85, 1);
    windowMat.emissiveColor = new Color3(0.05, 0.08, 0.1);
    windowMat.alpha = 0.6;

    const windowFrameMat = new StandardMaterial('window-frame-mat', this.scene);
    windowFrameMat.diffuseColor = new Color3(0.9, 0.9, 0.95);

    // Windows on left wall
    for (let i = 0; i < 4; i++) {
      const windowPane = MeshBuilder.CreateBox(
        `window-left-${i}`,
        { width: 0.1, height: 2, depth: 3 },
        this.scene
      );
      windowPane.position = new Vector3(-wallLength / 2 + 0.3, wallHeight / 2 + 1, -15 + i * 10);
      windowPane.material = windowMat;

      const frame = MeshBuilder.CreateBox(
        `window-frame-left-${i}`,
        { width: 0.2, height: 2.4, depth: 3.4 },
        this.scene
      );
      frame.position = new Vector3(-wallLength / 2 + 0.35, wallHeight / 2 + 1, -15 + i * 10);
      frame.material = windowFrameMat;
    }

    // Windows on right wall
    for (let i = 0; i < 4; i++) {
      const windowPane = MeshBuilder.CreateBox(
        `window-right-${i}`,
        { width: 0.1, height: 2, depth: 3 },
        this.scene
      );
      windowPane.position = new Vector3(wallLength / 2 - 0.3, wallHeight / 2 + 1, -15 + i * 10);
      windowPane.material = windowMat;

      const frame = MeshBuilder.CreateBox(
        `window-frame-right-${i}`,
        { width: 0.2, height: 2.4, depth: 3.4 },
        this.scene
      );
      frame.position = new Vector3(wallLength / 2 - 0.35, wallHeight / 2 + 1, -15 + i * 10);
      frame.material = windowFrameMat;
    }
  }

  private createFactorySign(): void {
    // Large sign above entrance
    const signMat = new StandardMaterial('sign-mat', this.scene);
    signMat.diffuseColor = new Color3(1, 0.4, 0.6);
    signMat.emissiveColor = new Color3(0.1, 0.02, 0.05);

    const sign = MeshBuilder.CreateBox(
      'factory-sign',
      { width: 16, height: 3, depth: 0.5 },
      this.scene
    );
    sign.position = new Vector3(0, 10, 25.5);
    sign.material = signMat;

    // Sign border
    const borderMat = new StandardMaterial('sign-border-mat', this.scene);
    borderMat.diffuseColor = new Color3(1, 0.85, 0);
    borderMat.emissiveColor = new Color3(0.08, 0.06, 0);

    const border = MeshBuilder.CreateBox(
      'sign-border',
      { width: 17, height: 3.5, depth: 0.3 },
      this.scene
    );
    border.position = new Vector3(0, 10, 25.3);
    border.material = borderMat;

    // Candy icon on sign
    const candyIcon = MeshBuilder.CreateSphere(
      'sign-candy',
      { diameter: 2, segments: 16 },
      this.scene
    );
    candyIcon.position = new Vector3(-6, 10, 26);

    const candyMat = new StandardMaterial('sign-candy-mat', this.scene);
    candyMat.diffuseColor = new Color3(1, 0.3, 0.5);
    candyMat.emissiveColor = new Color3(0.08, 0.02, 0.04);
    candyIcon.material = candyMat;
  }

  private createCandyShelves(): void {
    // Polished wood texture for shelves
    const shelfMat = new StandardMaterial('shelf-mat', this.scene);
    const woodTexture = new Texture('/assets/textures/tycoon/Wood052_1K-JPG_Color.jpg', this.scene);
    woodTexture.uScale = 2;
    woodTexture.vScale = 0.5;
    shelfMat.diffuseTexture = woodTexture;
    shelfMat.diffuseColor = new Color3(1, 0.95, 0.9); // Slight warm tint
    shelfMat.specularColor = new Color3(0.4, 0.35, 0.3);
    shelfMat.specularPower = 32;

    // Shelves along the back wall
    for (let i = 0; i < 3; i++) {
      // Shelf structure
      const shelf = MeshBuilder.CreateBox(
        `shelf-back-${i}`,
        { width: 12, height: 0.2, depth: 1.5 },
        this.scene
      );
      shelf.position = new Vector3(-15 + i * 15, 2.5, -23);
      shelf.material = shelfMat;

      // Second level
      const shelf2 = shelf.clone(`shelf-back-${i}-2`);
      shelf2.position.y = 4.5;

      // Shelf supports
      const supportMat = new StandardMaterial(`support-mat-${i}`, this.scene);
      supportMat.diffuseColor = new Color3(0.7, 0.5, 0.3);

      for (let j = -1; j <= 1; j += 2) {
        const support = MeshBuilder.CreateBox(
          `shelf-support-${i}-${j}`,
          { width: 0.3, height: 5, depth: 1.5 },
          this.scene
        );
        support.position = new Vector3(-15 + i * 15 + j * 5, 2.5, -23);
        support.material = supportMat;
      }

      // Add candy jars on shelves
      this.addCandyJars(new Vector3(-15 + i * 15, 2.7, -23), 5);
      this.addCandyJars(new Vector3(-15 + i * 15, 4.7, -23), 5);
    }

    // Side shelves
    for (let i = 0; i < 2; i++) {
      const sideShelf = MeshBuilder.CreateBox(
        `shelf-side-${i}`,
        { width: 1.5, height: 0.2, depth: 8 },
        this.scene
      );
      sideShelf.position = new Vector3(-23, 2.5, -10 + i * 15);
      sideShelf.material = shelfMat;

      this.addCandyJars(new Vector3(-23, 2.7, -10 + i * 15), 3, true);
    }
  }

  private addCandyJars(position: Vector3, count: number, vertical: boolean = false): void {
    const jarColors = [
      new Color3(1, 0.3, 0.4),   // Red
      new Color3(0.3, 0.8, 0.4), // Green
      new Color3(1, 0.8, 0.2),   // Yellow
      new Color3(0.4, 0.6, 1),   // Blue
      new Color3(1, 0.5, 0.8),   // Pink
    ];

    for (let i = 0; i < count; i++) {
      const jar = MeshBuilder.CreateCylinder(
        `candy-jar-${position.x}-${position.z}-${i}`,
        { height: 1.2, diameter: 0.8, tessellation: 16 },
        this.scene
      );

      const offset = (i - (count - 1) / 2) * 1.2;
      if (vertical) {
        jar.position = new Vector3(position.x, position.y + 0.6, position.z + offset);
      } else {
        jar.position = new Vector3(position.x + offset, position.y + 0.6, position.z);
      }

      const jarMat = new StandardMaterial(`jar-mat-${i}`, this.scene);
      jarMat.diffuseColor = new Color3(0.9, 0.9, 0.95);
      jarMat.alpha = 0.5;
      jarMat.specularColor = new Color3(1, 1, 1);
      jar.material = jarMat;

      // Candy inside jar
      const candy = MeshBuilder.CreateCylinder(
        `candy-inside-${position.x}-${position.z}-${i}`,
        { height: 0.8, diameter: 0.6, tessellation: 16 },
        this.scene
      );
      candy.position = jar.position.clone();
      candy.position.y -= 0.1;

      const candyMat = new StandardMaterial(`candy-mat-${i}`, this.scene);
      candyMat.diffuseColor = jarColors[i % jarColors.length];
      candyMat.emissiveColor = new Color3(
        jarColors[i % jarColors.length].r * 0.03,
        jarColors[i % jarColors.length].g * 0.03,
        jarColors[i % jarColors.length].b * 0.03
      );
      candy.material = candyMat;

      // Jar lid
      const lid = MeshBuilder.CreateCylinder(
        `jar-lid-${position.x}-${position.z}-${i}`,
        { height: 0.15, diameter: 0.9, tessellation: 16 },
        this.scene
      );
      lid.position = jar.position.clone();
      lid.position.y += 0.65;

      const lidMat = new StandardMaterial(`lid-mat-${i}`, this.scene);
      lidMat.diffuseColor = new Color3(0.85, 0.7, 0.5);
      lidMat.specularColor = new Color3(0.3, 0.2, 0.1);
      lid.material = lidMat;
    }
  }

  private createReceptionArea(): void {
    // Main counter with elegant pink tint
    const counterMat = new StandardMaterial('counter-mat', this.scene);
    counterMat.diffuseColor = new Color3(1, 0.85, 0.9);
    counterMat.specularColor = new Color3(0.3, 0.3, 0.3);

    const counter = MeshBuilder.CreateBox(
      'reception-counter',
      { width: 8, height: 1.2, depth: 2 },
      this.scene
    );
    counter.position = new Vector3(15, 0.6, 18);
    counter.material = counterMat;

    // Counter top with marble texture - elegant look
    const topMat = new StandardMaterial('counter-top-mat', this.scene);
    const marbleTexture = new Texture('/assets/textures/tycoon/Marble006_1K-JPG_Color.jpg', this.scene);
    marbleTexture.uScale = 2;
    marbleTexture.vScale = 1;
    topMat.diffuseTexture = marbleTexture;
    topMat.diffuseColor = new Color3(1, 0.97, 0.98); // Slight pink tint
    topMat.specularColor = new Color3(0.6, 0.6, 0.6);
    topMat.specularPower = 64;

    const counterTop = MeshBuilder.CreateBox(
      'counter-top',
      { width: 8.4, height: 0.15, depth: 2.4 },
      this.scene
    );
    counterTop.position = new Vector3(15, 1.25, 18);
    counterTop.material = topMat;

    // Cash register
    const registerMat = new StandardMaterial('register-mat', this.scene);
    registerMat.diffuseColor = new Color3(0.3, 0.3, 0.35);
    registerMat.specularColor = new Color3(0.4, 0.4, 0.4);

    const register = MeshBuilder.CreateBox(
      'cash-register',
      { width: 1.2, height: 0.8, depth: 1 },
      this.scene
    );
    register.position = new Vector3(15, 1.7, 18);
    register.material = registerMat;

    // Display case with treats
    this.createDisplayCase(new Vector3(15, 0, 15));
  }

  private createDisplayCase(position: Vector3): void {
    const glassMat = new StandardMaterial('glass-mat', this.scene);
    glassMat.diffuseColor = new Color3(0.9, 0.95, 1);
    glassMat.alpha = 0.3;
    glassMat.specularColor = new Color3(1, 1, 1);

    const displayCase = MeshBuilder.CreateBox(
      'display-case',
      { width: 4, height: 1.5, depth: 2 },
      this.scene
    );
    displayCase.position = new Vector3(position.x - 6, 0.75, position.z);
    displayCase.material = glassMat;

    // Add cupcakes/treats inside
    const cupcakeColors = [
      new Color3(1, 0.5, 0.6),
      new Color3(0.6, 0.4, 0.2),
      new Color3(1, 0.9, 0.5),
    ];

    for (let i = 0; i < 6; i++) {
      const row = Math.floor(i / 3);
      const col = i % 3;

      // Cupcake base
      const base = MeshBuilder.CreateCylinder(
        `cupcake-base-${i}`,
        { height: 0.4, diameterTop: 0.5, diameterBottom: 0.4, tessellation: 16 },
        this.scene
      );
      base.position = new Vector3(
        position.x - 7.2 + col * 1.2,
        0.4 + row * 0.6,
        position.z - 0.3 + row * 0.6
      );

      const baseMat = new StandardMaterial(`cupcake-base-mat-${i}`, this.scene);
      baseMat.diffuseColor = new Color3(0.6, 0.4, 0.2);
      base.material = baseMat;

      // Frosting
      const frosting = MeshBuilder.CreateSphere(
        `cupcake-frosting-${i}`,
        { diameter: 0.5, segments: 12 },
        this.scene
      );
      frosting.position = base.position.clone();
      frosting.position.y += 0.35;
      frosting.scaling.y = 0.6;

      const frostingMat = new StandardMaterial(`frosting-mat-${i}`, this.scene);
      frostingMat.diffuseColor = cupcakeColors[col];
      frostingMat.emissiveColor = new Color3(
        cupcakeColors[col].r * 0.02,
        cupcakeColors[col].g * 0.02,
        cupcakeColors[col].b * 0.02
      );
      frosting.material = frostingMat;
    }
  }

  private createVendorNPC(): void {
    // Body
    const bodyMat = new StandardMaterial('vendor-body-mat', this.scene);
    bodyMat.diffuseColor = new Color3(1, 0.6, 0.7);

    const body = MeshBuilder.CreateCylinder(
      'vendor-body',
      { height: 1.5, diameter: 0.8, tessellation: 16 },
      this.scene
    );
    body.position = new Vector3(17, 0.75, 19);
    body.material = bodyMat;

    // Head
    const headMat = new StandardMaterial('vendor-head-mat', this.scene);
    headMat.diffuseColor = new Color3(1, 0.85, 0.75);

    const head = MeshBuilder.CreateSphere(
      'vendor-head',
      { diameter: 0.7, segments: 16 },
      this.scene
    );
    head.position = new Vector3(17, 1.85, 19);
    head.material = headMat;

    // Chef hat
    const hatMat = new StandardMaterial('vendor-hat-mat', this.scene);
    hatMat.diffuseColor = new Color3(1, 1, 1);

    const hatBase = MeshBuilder.CreateCylinder(
      'vendor-hat-base',
      { height: 0.15, diameter: 0.8, tessellation: 16 },
      this.scene
    );
    hatBase.position = new Vector3(17, 2.25, 19);
    hatBase.material = hatMat;

    const hatTop = MeshBuilder.CreateCylinder(
      'vendor-hat-top',
      { height: 0.5, diameter: 0.6, tessellation: 16 },
      this.scene
    );
    hatTop.position = new Vector3(17, 2.55, 19);
    hatTop.material = hatMat;

    // Arms
    const armMat = new StandardMaterial('vendor-arm-mat', this.scene);
    armMat.diffuseColor = new Color3(1, 0.6, 0.7);

    const leftArm = MeshBuilder.CreateCylinder(
      'vendor-left-arm',
      { height: 0.8, diameter: 0.2, tessellation: 8 },
      this.scene
    );
    leftArm.position = new Vector3(16.5, 1.2, 19);
    leftArm.rotation.z = Math.PI / 4;
    leftArm.material = armMat;

    const rightArm = MeshBuilder.CreateCylinder(
      'vendor-right-arm',
      { height: 0.8, diameter: 0.2, tessellation: 8 },
      this.scene
    );
    rightArm.position = new Vector3(17.5, 1.2, 19);
    rightArm.rotation.z = -Math.PI / 4;
    rightArm.material = armMat;

    // Eyes
    const eyeMat = new StandardMaterial('vendor-eye-mat', this.scene);
    eyeMat.diffuseColor = new Color3(0.1, 0.1, 0.1);

    const leftEye = MeshBuilder.CreateSphere(
      'vendor-left-eye',
      { diameter: 0.1, segments: 8 },
      this.scene
    );
    leftEye.position = new Vector3(16.85, 1.9, 19.3);
    leftEye.material = eyeMat;

    const rightEye = leftEye.clone('vendor-right-eye');
    rightEye.position.x = 17.15;

    // Smile
    const smileMat = new StandardMaterial('vendor-smile-mat', this.scene);
    smileMat.diffuseColor = new Color3(0.8, 0.3, 0.3);

    const smile = MeshBuilder.CreateTorus(
      'vendor-smile',
      { diameter: 0.25, thickness: 0.05, tessellation: 16 },
      this.scene
    );
    smile.position = new Vector3(17, 1.75, 19.3);
    smile.rotation.x = Math.PI / 2;
    smile.scaling.y = 0.5;
    smile.material = smileMat;

    // Animate vendor (slight bobbing)
    const bobAnim = new Animation(
      'vendor-bob',
      'position.y',
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );
    bobAnim.setKeys([
      { frame: 0, value: body.position.y },
      { frame: 30, value: body.position.y + 0.1 },
      { frame: 60, value: body.position.y },
    ]);
    body.animations.push(bobAnim);
    this.scene.beginAnimation(body, 0, 60, true);
  }

  private createHangingDecorations(): void {
    // Hanging candy decorations from ceiling
    const candyColors = [
      new Color3(1, 0.3, 0.4),
      new Color3(0.3, 0.8, 0.5),
      new Color3(1, 0.8, 0.2),
      new Color3(0.5, 0.7, 1),
      new Color3(1, 0.5, 0.8),
    ];

    // Lollipops hanging from ceiling
    for (let i = 0; i < 8; i++) {
      const x = -15 + (i % 4) * 10;
      const z = -10 + Math.floor(i / 4) * 20;

      // String
      const string = MeshBuilder.CreateCylinder(
        `lollipop-string-${i}`,
        { height: 2, diameter: 0.05 },
        this.scene
      );
      string.position = new Vector3(x, 7, z);

      const stringMat = new StandardMaterial(`string-mat-${i}`, this.scene);
      stringMat.diffuseColor = new Color3(0.8, 0.8, 0.8);
      string.material = stringMat;

      // Lollipop candy
      const candy = MeshBuilder.CreateSphere(
        `hanging-candy-${i}`,
        { diameter: 1.5, segments: 16 },
        this.scene
      );
      candy.position = new Vector3(x, 5.5, z);

      const candyMat = new StandardMaterial(`hanging-candy-mat-${i}`, this.scene);
      candyMat.diffuseColor = candyColors[i % candyColors.length];
      candyMat.emissiveColor = new Color3(
        candyColors[i % candyColors.length].r * 0.02,
        candyColors[i % candyColors.length].g * 0.02,
        candyColors[i % candyColors.length].b * 0.02
      );
      candy.material = candyMat;

      // Rotate animation
      const rotateAnim = new Animation(
        `candy-rotate-${i}`,
        'rotation.y',
        15 + i * 2,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE
      );
      rotateAnim.setKeys([
        { frame: 0, value: 0 },
        { frame: 120, value: Math.PI * 2 },
      ]);
      candy.animations.push(rotateAnim);
      this.scene.beginAnimation(candy, 0, 120, true);
    }

    // Bunting/banner decorations
    this.createBunting();
  }

  private createBunting(): void {
    const flagColors = [
      new Color3(1, 0.4, 0.5),
      new Color3(1, 0.9, 0.3),
      new Color3(0.4, 0.8, 1),
      new Color3(0.5, 1, 0.5),
      new Color3(1, 0.6, 0.9),
    ];

    // Create bunting along walls
    for (let wall = 0; wall < 2; wall++) {
      const z = wall === 0 ? -20 : 20;

      for (let i = 0; i < 10; i++) {
        const flag = MeshBuilder.CreateBox(
          `bunting-${wall}-${i}`,
          { width: 1.5, height: 1, depth: 0.05 },
          this.scene
        );
        flag.position = new Vector3(-20 + i * 4.5, 6.5, z);
        flag.rotation.z = Math.PI / 6 * (i % 2 === 0 ? 1 : -1);

        const flagMat = new StandardMaterial(`bunting-mat-${wall}-${i}`, this.scene);
        flagMat.diffuseColor = flagColors[i % flagColors.length];
        flagMat.emissiveColor = new Color3(
          flagColors[i % flagColors.length].r * 0.02,
          flagColors[i % flagColors.length].g * 0.02,
          flagColors[i % flagColors.length].b * 0.02
        );
        flag.material = flagMat;
      }
    }
  }

  private createCandyDisplays(): void {
    // Giant candy decorations around the factory

    // Giant lollipop
    const lollipopStick = MeshBuilder.CreateCylinder(
      'giant-lollipop-stick',
      { height: 4, diameter: 0.3 },
      this.scene
    );
    lollipopStick.position = new Vector3(-20, 2, 10);

    const stickMat = new StandardMaterial('lollipop-stick-mat', this.scene);
    stickMat.diffuseColor = new Color3(0.9, 0.85, 0.8);
    lollipopStick.material = stickMat;

    const lollipopHead = MeshBuilder.CreateSphere(
      'giant-lollipop-head',
      { diameter: 3, segments: 24 },
      this.scene
    );
    lollipopHead.position = new Vector3(-20, 5.5, 10);

    const lollipopMat = new StandardMaterial('giant-lollipop-mat', this.scene);
    lollipopMat.diffuseColor = new Color3(1, 0.3, 0.5);
    lollipopMat.emissiveColor = new Color3(0.05, 0.02, 0.03);
    lollipopHead.material = lollipopMat;

    // Giant cupcake
    const cupcakeBase = MeshBuilder.CreateCylinder(
      'giant-cupcake-base',
      { height: 2, diameterTop: 2.5, diameterBottom: 2, tessellation: 24 },
      this.scene
    );
    cupcakeBase.position = new Vector3(20, 1, -15);

    const cupcakeBaseMat = new StandardMaterial('giant-cupcake-base-mat', this.scene);
    cupcakeBaseMat.diffuseColor = new Color3(0.7, 0.5, 0.3);
    cupcakeBase.material = cupcakeBaseMat;

    const cupcakeFrosting = MeshBuilder.CreateSphere(
      'giant-cupcake-frosting',
      { diameter: 3, segments: 24 },
      this.scene
    );
    cupcakeFrosting.position = new Vector3(20, 3, -15);
    cupcakeFrosting.scaling.y = 0.7;

    const frostingMat = new StandardMaterial('giant-frosting-mat', this.scene);
    frostingMat.diffuseColor = new Color3(1, 0.7, 0.8);
    frostingMat.emissiveColor = new Color3(0.05, 0.02, 0.03);
    cupcakeFrosting.material = frostingMat;

    // Cherry on top
    const cherry = MeshBuilder.CreateSphere(
      'giant-cherry',
      { diameter: 0.8, segments: 16 },
      this.scene
    );
    cherry.position = new Vector3(20, 4.2, -15);

    const cherryMat = new StandardMaterial('giant-cherry-mat', this.scene);
    cherryMat.diffuseColor = new Color3(0.8, 0.1, 0.2);
    cherryMat.emissiveColor = new Color3(0.05, 0, 0.01);
    cherry.material = cherryMat;

    // Ice cream cone
    const cone = MeshBuilder.CreateCylinder(
      'giant-cone',
      { height: 3, diameterTop: 2, diameterBottom: 0.3, tessellation: 16 },
      this.scene
    );
    cone.position = new Vector3(-20, 1.5, -15);

    const coneMat = new StandardMaterial('giant-cone-mat', this.scene);
    coneMat.diffuseColor = new Color3(0.85, 0.7, 0.4);
    cone.material = coneMat;

    const iceCream = MeshBuilder.CreateSphere(
      'giant-ice-cream',
      { diameter: 2.5, segments: 24 },
      this.scene
    );
    iceCream.position = new Vector3(-20, 4, -15);

    const iceCreamMat = new StandardMaterial('giant-ice-cream-mat', this.scene);
    iceCreamMat.diffuseColor = new Color3(1, 0.95, 0.85);
    iceCreamMat.emissiveColor = new Color3(0.02, 0.02, 0.02);
    iceCream.material = iceCreamMat;
  }

  private createGlassVitrines(): void {
    // Glass material for display cases
    const glassMat = new StandardMaterial('vitrine-glass-mat', this.scene);
    glassMat.diffuseColor = new Color3(0.85, 0.95, 1);
    glassMat.specularColor = new Color3(1, 1, 1);
    glassMat.specularPower = 128;
    glassMat.alpha = 0.25;
    glassMat.backFaceCulling = false;

    // Wood frame material
    const frameMat = new StandardMaterial('vitrine-frame-mat', this.scene);
    frameMat.diffuseColor = new Color3(0.6, 0.35, 0.2);
    frameMat.specularColor = new Color3(0.3, 0.2, 0.15);

    // White base material
    const baseMat = new StandardMaterial('vitrine-base-mat', this.scene);
    baseMat.diffuseColor = new Color3(1, 0.98, 0.95);
    baseMat.specularColor = new Color3(0.2, 0.2, 0.2);

    // Vitrine positions near the entrance
    const vitrinePositions = [
      { x: -8, z: 18 },  // Left of entrance
      { x: 8, z: 18 },   // Right of entrance
    ];

    vitrinePositions.forEach((pos, index) => {
      // Base/counter
      const base = MeshBuilder.CreateBox(
        `vitrine-base-${index}`,
        { width: 4, height: 1, depth: 2 },
        this.scene
      );
      base.position = new Vector3(pos.x, 0.5, pos.z);
      base.material = baseMat;

      // Glass case
      const glassCase = MeshBuilder.CreateBox(
        `vitrine-glass-${index}`,
        { width: 3.6, height: 2, depth: 1.6 },
        this.scene
      );
      glassCase.position = new Vector3(pos.x, 2, pos.z);
      glassCase.material = glassMat;

      // Top frame
      const topFrame = MeshBuilder.CreateBox(
        `vitrine-top-${index}`,
        { width: 4, height: 0.15, depth: 2 },
        this.scene
      );
      topFrame.position = new Vector3(pos.x, 3.1, pos.z);
      topFrame.material = frameMat;

      // Corner frames (vertical)
      const corners = [
        { x: -1.85, z: -0.85 },
        { x: 1.85, z: -0.85 },
        { x: -1.85, z: 0.85 },
        { x: 1.85, z: 0.85 },
      ];

      corners.forEach((corner, ci) => {
        const frame = MeshBuilder.CreateBox(
          `vitrine-corner-${index}-${ci}`,
          { width: 0.15, height: 2.2, depth: 0.15 },
          this.scene
        );
        frame.position = new Vector3(pos.x + corner.x, 2, pos.z + corner.z);
        frame.material = frameMat;
      });

      // Add colorful candies inside the vitrine
      const candyColors = [
        new Color3(1, 0.3, 0.4),   // Red
        new Color3(0.3, 0.9, 0.4), // Green
        new Color3(1, 0.85, 0.2),  // Yellow
        new Color3(0.4, 0.6, 1),   // Blue
        new Color3(1, 0.5, 0.8),   // Pink
        new Color3(1, 0.6, 0.2),   // Orange
      ];

      // Small candy spheres inside
      for (let i = 0; i < 8; i++) {
        const candy = MeshBuilder.CreateSphere(
          `vitrine-candy-${index}-${i}`,
          { diameter: 0.35, segments: 12 },
          this.scene
        );
        const offsetX = (Math.random() - 0.5) * 2.5;
        const offsetZ = (Math.random() - 0.5) * 1;
        const offsetY = Math.random() * 0.5;
        candy.position = new Vector3(pos.x + offsetX, 1.3 + offsetY, pos.z + offsetZ);

        const candyMat = new StandardMaterial(`vitrine-candy-mat-${index}-${i}`, this.scene);
        candyMat.diffuseColor = candyColors[i % candyColors.length];
        candyMat.emissiveColor = new Color3(
          candyColors[i % candyColors.length].r * 0.1,
          candyColors[i % candyColors.length].g * 0.1,
          candyColors[i % candyColors.length].b * 0.1
        );
        candy.material = candyMat;
      }

      // Lollipops sticking up inside
      for (let i = 0; i < 3; i++) {
        const stick = MeshBuilder.CreateCylinder(
          `vitrine-lollipop-stick-${index}-${i}`,
          { height: 1.2, diameter: 0.08 },
          this.scene
        );
        const offsetX = -1 + i * 1;
        stick.position = new Vector3(pos.x + offsetX, 1.8, pos.z);

        const stickMat = new StandardMaterial(`vitrine-stick-mat-${index}-${i}`, this.scene);
        stickMat.diffuseColor = new Color3(0.9, 0.85, 0.75);
        stick.material = stickMat;

        const head = MeshBuilder.CreateSphere(
          `vitrine-lollipop-head-${index}-${i}`,
          { diameter: 0.5, segments: 16 },
          this.scene
        );
        head.position = new Vector3(pos.x + offsetX, 2.5, pos.z);

        const headMat = new StandardMaterial(`vitrine-head-mat-${index}-${i}`, this.scene);
        headMat.diffuseColor = candyColors[(i * 2) % candyColors.length];
        headMat.emissiveColor = new Color3(0.05, 0.02, 0.02);
        head.material = headMat;
      }
    });

    // Central display case (larger, in the middle-front area)
    const centralBase = MeshBuilder.CreateBox(
      'vitrine-central-base',
      { width: 6, height: 1.2, depth: 3 },
      this.scene
    );
    centralBase.position = new Vector3(0, 0.6, 12);
    centralBase.material = baseMat;

    const centralGlass = MeshBuilder.CreateBox(
      'vitrine-central-glass',
      { width: 5.5, height: 2.5, depth: 2.5 },
      this.scene
    );
    centralGlass.position = new Vector3(0, 2.5, 12);
    centralGlass.material = glassMat;

    const centralTop = MeshBuilder.CreateBox(
      'vitrine-central-top',
      { width: 6, height: 0.2, depth: 3 },
      this.scene
    );
    centralTop.position = new Vector3(0, 3.85, 12);
    centralTop.material = frameMat;

    // Corner frames for central vitrine
    const centralCorners = [
      { x: -2.7, z: -1.2 },
      { x: 2.7, z: -1.2 },
      { x: -2.7, z: 1.2 },
      { x: 2.7, z: 1.2 },
    ];

    centralCorners.forEach((corner, ci) => {
      const frame = MeshBuilder.CreateBox(
        `vitrine-central-corner-${ci}`,
        { width: 0.2, height: 2.7, depth: 0.2 },
        this.scene
      );
      frame.position = new Vector3(corner.x, 2.5, 12 + corner.z);
      frame.material = frameMat;
    });

    // Giant candy display inside central vitrine
    const giantCandy = MeshBuilder.CreateSphere(
      'vitrine-giant-candy',
      { diameter: 1.5, segments: 24 },
      this.scene
    );
    giantCandy.position = new Vector3(0, 2.2, 12);

    const giantCandyMat = new StandardMaterial('vitrine-giant-candy-mat', this.scene);
    giantCandyMat.diffuseColor = new Color3(1, 0.2, 0.4);
    giantCandyMat.emissiveColor = new Color3(0.1, 0.02, 0.03);
    giantCandyMat.specularColor = new Color3(0.8, 0.8, 0.8);
    giantCandy.material = giantCandyMat;

    // Ribbon on giant candy
    const ribbon = MeshBuilder.CreateTorus(
      'vitrine-ribbon',
      { diameter: 1.6, thickness: 0.15, tessellation: 32 },
      this.scene
    );
    ribbon.position = new Vector3(0, 2.2, 12);
    ribbon.rotation.x = Math.PI / 2;

    const ribbonMat = new StandardMaterial('vitrine-ribbon-mat', this.scene);
    ribbonMat.diffuseColor = new Color3(1, 0.85, 0.2);
    ribbonMat.emissiveColor = new Color3(0.1, 0.08, 0.01);
    ribbon.material = ribbonMat;
  }

  private createDecorations(): void {
    // Candy pillars
    const pillarPositions = [
      new Vector3(-18, 0, -18),
      new Vector3(18, 0, -18),
      new Vector3(-18, 0, 18),
      new Vector3(18, 0, 18),
    ];

    const pillarColors = [
      new Color3(1, 0.4, 0.6),
      new Color3(0.4, 0.8, 1),
      new Color3(1, 0.8, 0.3),
      new Color3(0.6, 1, 0.5),
    ];

    pillarPositions.forEach((pos, i) => {
      this.createCandyPillar(pos, pillarColors[i], i);
    });

    // Central display platform
    this.createDisplayPlatform();
  }

  private createCandyPillar(position: Vector3, color: Color3, index: number): void {
    // Spiral candy pillar
    const pillar = MeshBuilder.CreateCylinder(
      `pillar-${index}`,
      { height: 4, diameter: 1.2, tessellation: 24 },
      this.scene
    );
    pillar.position = new Vector3(position.x, 2, position.z);

    const pillarMat = new StandardMaterial(`pillar-mat-${index}`, this.scene);
    pillarMat.diffuseColor = color;
    pillarMat.specularColor = new Color3(0.5, 0.5, 0.5);
    pillarMat.specularPower = 32;
    pillar.material = pillarMat;

    // Top sphere (like a lollipop)
    const top = MeshBuilder.CreateSphere(
      `pillar-top-${index}`,
      { diameter: 1.8, segments: 16 },
      this.scene
    );
    top.position = new Vector3(position.x, 4.5, position.z);

    const topMat = new StandardMaterial(`pillar-top-mat-${index}`, this.scene);
    topMat.diffuseColor = new Color3(
      Math.min(1, color.r + 0.2),
      Math.min(1, color.g + 0.2),
      Math.min(1, color.b + 0.2)
    );
    topMat.emissiveColor = new Color3(color.r * 0.05, color.g * 0.05, color.b * 0.05);
    top.material = topMat;

    // Animate the top sphere
    const rotateAnim = new Animation(
      `pillar-rotate-${index}`,
      'rotation.y',
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );
    rotateAnim.setKeys([
      { frame: 0, value: 0 },
      { frame: 120, value: Math.PI * 2 },
    ]);
    top.animations.push(rotateAnim);
    this.scene.beginAnimation(top, 0, 120, true);
  }

  private createDisplayPlatform(): void {
    // Central raised platform where main production happens
    const platform = MeshBuilder.CreateCylinder(
      'display-platform',
      { height: 0.3, diameter: 8, tessellation: 32 },
      this.scene
    );
    platform.position = new Vector3(0, 0.15, 0);

    const platMat = new StandardMaterial('platform-mat', this.scene);
    platMat.diffuseColor = new Color3(0.9, 0.85, 0.95);
    platMat.specularColor = new Color3(0.3, 0.3, 0.3);
    platform.material = platMat;

    // Glowing ring
    const ring = MeshBuilder.CreateTorus(
      'platform-ring',
      { diameter: 8.5, thickness: 0.2, tessellation: 48 },
      this.scene
    );
    ring.position = new Vector3(0, 0.35, 0);

    const ringMat = new StandardMaterial('ring-mat', this.scene);
    ringMat.diffuseColor = new Color3(1, 0.8, 0.3);
    ringMat.emissiveColor = new Color3(0.1, 0.08, 0.02);
    ring.material = ringMat;
  }

  public createMachine(
    type: MachineType,
    position: Vector3,
    level: number = 1
  ): PlacedMachine {
    const config = MACHINE_CONFIGS[type];
    const id = `machine-${this.machineIdCounter++}`;

    // Create machine base with rubber conveyor belt texture
    const base = MeshBuilder.CreateBox(
      `${id}-base`,
      { width: 2, height: 0.3, depth: 2 },
      this.scene
    );
    base.position = new Vector3(position.x, 0.15, position.z);

    const baseMat = new StandardMaterial(`${id}-base-mat`, this.scene);
    const rubberTexture = new Texture('/assets/textures/tycoon/Rubber004_1K-JPG_Color.jpg', this.scene);
    rubberTexture.uScale = 3;
    rubberTexture.vScale = 3;
    baseMat.diffuseTexture = rubberTexture;
    baseMat.diffuseColor = new Color3(0.35, 0.35, 0.4);
    baseMat.specularColor = new Color3(0.2, 0.2, 0.2);
    baseMat.specularPower = 16;
    base.material = baseMat;

    // Create machine body based on type
    const body = this.createMachineBody(id, type, config);
    body.position = new Vector3(position.x, 1.2, position.z);

    // Scale based on level
    const scale = 1 + (level - 1) * 0.1;
    body.scaling = new Vector3(scale, scale, scale);

    // Parent body to base
    body.parent = null;

    // Create particle effects
    const particles = this.createMachineParticles(id, type, config, position);

    // Animate the machine
    this.animateMachine(body, type);

    const machine: PlacedMachine = {
      id,
      type,
      mesh: body,
      position,
      level,
      particles,
    };

    this.machines.set(id, machine);
    return machine;
  }

  private createMachineBody(
    id: string,
    type: MachineType,
    config: MachineConfig
  ): Mesh {
    let body: Mesh;

    switch (type) {
      case MachineType.CANDY_MIXER:
        body = this.createMixerMachine(id, config);
        break;
      case MachineType.LOLLIPOP_MAKER:
        body = this.createLollipopMachine(id, config);
        break;
      case MachineType.CHOCOLATE_FOUNTAIN:
        body = this.createFountainMachine(id, config);
        break;
      case MachineType.GUMMY_PRESS:
        body = this.createPressMachine(id, config);
        break;
      case MachineType.COOKIE_OVEN:
        body = this.createOvenMachine(id, config);
        break;
      case MachineType.ICE_CREAM_MACHINE:
        body = this.createIceCreamMachine(id, config);
        break;
      case MachineType.CAKE_FACTORY:
        body = this.createCakeMachine(id, config);
        break;
      case MachineType.DONUT_FRYER:
        body = this.createDonutMachine(id, config);
        break;
      case MachineType.RAINBOW_GENERATOR:
        body = this.createRainbowMachine(id, config);
        break;
      default:
        body = MeshBuilder.CreateBox(id, { size: 1.5 }, this.scene);
    }

    return body;
  }

  private createMixerMachine(id: string, config: MachineConfig): Mesh {
    // Bowl shape
    const bowl = MeshBuilder.CreateCylinder(
      `${id}-bowl`,
      { height: 1.2, diameterTop: 1.8, diameterBottom: 1.2, tessellation: 24 },
      this.scene
    );

    const bowlMat = new StandardMaterial(`${id}-bowl-mat`, this.scene);
    bowlMat.diffuseColor = config.color;
    bowlMat.specularColor = new Color3(0.6, 0.6, 0.6);
    bowlMat.specularPower = 64;
    bowl.material = bowlMat;

    // Mixer arm
    const arm = MeshBuilder.CreateCylinder(
      `${id}-arm`,
      { height: 1, diameter: 0.15 },
      this.scene
    );
    arm.position.y = 1;
    arm.parent = bowl;

    const armMat = new StandardMaterial(`${id}-arm-mat`, this.scene);
    armMat.diffuseColor = new Color3(0.7, 0.7, 0.75);
    armMat.specularColor = new Color3(0.8, 0.8, 0.8);
    arm.material = armMat;

    return bowl;
  }

  private createLollipopMachine(id: string, config: MachineConfig): Mesh {
    // Cylindrical body
    const body = MeshBuilder.CreateCylinder(
      `${id}-body`,
      { height: 1.8, diameter: 1.4, tessellation: 24 },
      this.scene
    );

    const bodyMat = new StandardMaterial(`${id}-body-mat`, this.scene);
    bodyMat.diffuseColor = config.color;
    bodyMat.specularColor = new Color3(0.5, 0.5, 0.5);
    body.material = bodyMat;

    // Top dome
    const dome = MeshBuilder.CreateSphere(
      `${id}-dome`,
      { diameter: 1.5, segments: 16, slice: 0.5 },
      this.scene
    );
    dome.position.y = 0.9;
    dome.parent = body;

    const domeMat = new StandardMaterial(`${id}-dome-mat`, this.scene);
    domeMat.diffuseColor = config.accentColor;
    domeMat.alpha = 0.8;
    dome.material = domeMat;

    return body;
  }

  private createFountainMachine(id: string, config: MachineConfig): Mesh {
    // Fountain base
    const base = MeshBuilder.CreateCylinder(
      `${id}-fountain-base`,
      { height: 0.5, diameterTop: 2, diameterBottom: 1.8, tessellation: 24 },
      this.scene
    );

    const baseMat = new StandardMaterial(`${id}-base-mat`, this.scene);
    baseMat.diffuseColor = config.color;
    baseMat.specularColor = new Color3(0.4, 0.3, 0.2);
    base.material = baseMat;

    // Tiers
    for (let i = 1; i <= 3; i++) {
      const tier = MeshBuilder.CreateCylinder(
        `${id}-tier-${i}`,
        {
          height: 0.3,
          diameterTop: 1.8 - i * 0.4,
          diameterBottom: 2 - i * 0.4,
          tessellation: 24,
        },
        this.scene
      );
      tier.position.y = 0.3 + i * 0.4;
      tier.parent = base;
      tier.material = baseMat;
    }

    return base;
  }

  private createPressMachine(id: string, config: MachineConfig): Mesh {
    // Box-shaped press
    const body = MeshBuilder.CreateBox(
      `${id}-press`,
      { width: 1.6, height: 1.4, depth: 1.6 },
      this.scene
    );

    const bodyMat = new StandardMaterial(`${id}-body-mat`, this.scene);
    bodyMat.diffuseColor = config.color;
    bodyMat.specularColor = new Color3(0.3, 0.3, 0.3);
    body.material = bodyMat;

    // Press plate on top
    const plate = MeshBuilder.CreateBox(
      `${id}-plate`,
      { width: 1.2, height: 0.2, depth: 1.2 },
      this.scene
    );
    plate.position.y = 0.9;
    plate.parent = body;

    const plateMat = new StandardMaterial(`${id}-plate-mat`, this.scene);
    plateMat.diffuseColor = new Color3(0.6, 0.6, 0.65);
    plateMat.specularColor = new Color3(0.7, 0.7, 0.7);
    plate.material = plateMat;

    return body;
  }

  private createOvenMachine(id: string, config: MachineConfig): Mesh {
    // Oven body
    const body = MeshBuilder.CreateBox(
      `${id}-oven`,
      { width: 1.8, height: 1.5, depth: 1.4 },
      this.scene
    );

    const bodyMat = new StandardMaterial(`${id}-body-mat`, this.scene);
    bodyMat.diffuseColor = config.color;
    bodyMat.specularColor = new Color3(0.3, 0.3, 0.3);
    body.material = bodyMat;

    // Oven door (glass)
    const door = MeshBuilder.CreateBox(
      `${id}-door`,
      { width: 1.2, height: 0.8, depth: 0.1 },
      this.scene
    );
    door.position = new Vector3(0, -0.1, 0.7);
    door.parent = body;

    const doorMat = new StandardMaterial(`${id}-door-mat`, this.scene);
    doorMat.diffuseColor = new Color3(0.3, 0.2, 0.1);
    doorMat.alpha = 0.7;
    doorMat.emissiveColor = new Color3(0.06, 0.03, 0.01);
    door.material = doorMat;

    return body;
  }

  private createIceCreamMachine(id: string, config: MachineConfig): Mesh {
    // Tall cylindrical body
    const body = MeshBuilder.CreateCylinder(
      `${id}-body`,
      { height: 2, diameter: 1.2, tessellation: 24 },
      this.scene
    );

    const bodyMat = new StandardMaterial(`${id}-body-mat`, this.scene);
    bodyMat.diffuseColor = config.color;
    bodyMat.specularColor = new Color3(0.6, 0.6, 0.6);
    body.material = bodyMat;

    // Dispenser nozzle
    const nozzle = MeshBuilder.CreateCylinder(
      `${id}-nozzle`,
      { height: 0.4, diameterTop: 0.3, diameterBottom: 0.15 },
      this.scene
    );
    nozzle.position = new Vector3(0.5, -0.6, 0);
    nozzle.rotation.z = Math.PI / 4;
    nozzle.parent = body;

    const nozzleMat = new StandardMaterial(`${id}-nozzle-mat`, this.scene);
    nozzleMat.diffuseColor = new Color3(0.8, 0.8, 0.85);
    nozzle.material = nozzleMat;

    return body;
  }

  private createCakeMachine(id: string, config: MachineConfig): Mesh {
    // Large factory unit
    const body = MeshBuilder.CreateBox(
      `${id}-factory`,
      { width: 2.2, height: 1.8, depth: 2.2 },
      this.scene
    );

    const bodyMat = new StandardMaterial(`${id}-body-mat`, this.scene);
    bodyMat.diffuseColor = config.color;
    bodyMat.specularColor = new Color3(0.4, 0.4, 0.4);
    body.material = bodyMat;

    // Chimney
    const chimney = MeshBuilder.CreateCylinder(
      `${id}-chimney`,
      { height: 0.8, diameter: 0.4 },
      this.scene
    );
    chimney.position = new Vector3(0.6, 1.2, 0.6);
    chimney.parent = body;

    const chimneyMat = new StandardMaterial(`${id}-chimney-mat`, this.scene);
    chimneyMat.diffuseColor = new Color3(0.5, 0.5, 0.55);
    chimney.material = chimneyMat;

    return body;
  }

  private createDonutMachine(id: string, config: MachineConfig): Mesh {
    // Fryer body
    const body = MeshBuilder.CreateBox(
      `${id}-fryer`,
      { width: 1.8, height: 1.2, depth: 1.6 },
      this.scene
    );

    const bodyMat = new StandardMaterial(`${id}-body-mat`, this.scene);
    bodyMat.diffuseColor = config.color;
    bodyMat.specularColor = new Color3(0.5, 0.4, 0.3);
    body.material = bodyMat;

    // Oil surface (shiny)
    const oil = MeshBuilder.CreateBox(
      `${id}-oil`,
      { width: 1.4, height: 0.1, depth: 1.2 },
      this.scene
    );
    oil.position.y = 0.5;
    oil.parent = body;

    const oilMat = new StandardMaterial(`${id}-oil-mat`, this.scene);
    oilMat.diffuseColor = new Color3(0.6, 0.4, 0.1);
    oilMat.specularColor = new Color3(0.9, 0.8, 0.5);
    oilMat.specularPower = 128;
    oil.material = oilMat;

    return body;
  }

  private createRainbowMachine(id: string, config: MachineConfig): Mesh {
    // Magical machine with crystal
    const body = MeshBuilder.CreateCylinder(
      `${id}-body`,
      { height: 1.5, diameter: 1.8, tessellation: 6 },
      this.scene
    );

    const bodyMat = new StandardMaterial(`${id}-body-mat`, this.scene);
    bodyMat.diffuseColor = config.color;
    bodyMat.specularColor = new Color3(0.8, 0.6, 1);
    bodyMat.emissiveColor = new Color3(0.04, 0.02, 0.06);
    body.material = bodyMat;

    // Crystal on top
    const crystal = MeshBuilder.CreatePolyhedron(
      `${id}-crystal`,
      { type: 1, size: 0.5 },
      this.scene
    );
    crystal.position.y = 1.2;
    crystal.parent = body;

    const crystalMat = new StandardMaterial(`${id}-crystal-mat`, this.scene);
    crystalMat.diffuseColor = new Color3(1, 0.8, 1);
    crystalMat.specularColor = new Color3(1, 1, 1);
    crystalMat.emissiveColor = new Color3(0.1, 0.06, 0.12);
    crystalMat.alpha = 0.85;
    crystal.material = crystalMat;

    return body;
  }

  private createMachineParticles(
    id: string,
    _type: MachineType,
    config: MachineConfig,
    position: Vector3
  ): ParticleSystem {
    const particles = new ParticleSystem(`${id}-particles`, 50, this.scene);

    // Use a simple texture (we'll create it procedurally)
    particles.particleTexture = new Texture(
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAADED76LAAAAGklEQVQYV2NkYGD4z4AHMP7//x8fN1jEwNAAAGccAwLVcqsnAAAAAElFTkSuQmCC',
      this.scene
    );

    particles.emitter = new Vector3(position.x, 2.5, position.z);
    particles.minEmitBox = new Vector3(-0.3, 0, -0.3);
    particles.maxEmitBox = new Vector3(0.3, 0.5, 0.3);

    particles.color1 = new Color4(config.color.r, config.color.g, config.color.b, 1);
    particles.color2 = new Color4(
      config.accentColor.r,
      config.accentColor.g,
      config.accentColor.b,
      1
    );
    particles.colorDead = new Color4(1, 1, 1, 0);

    particles.minSize = 0.1;
    particles.maxSize = 0.25;

    particles.minLifeTime = 1;
    particles.maxLifeTime = 2;

    particles.emitRate = 8;

    particles.direction1 = new Vector3(-0.5, 1, -0.5);
    particles.direction2 = new Vector3(0.5, 2, 0.5);

    particles.gravity = new Vector3(0, -0.5, 0);

    particles.start();

    return particles;
  }

  private animateMachine(mesh: Mesh, type: MachineType): void {
    let animationType = 'bounce';
    let speed = 30;

    switch (type) {
      case MachineType.CANDY_MIXER:
        animationType = 'rotate';
        speed = 60;
        break;
      case MachineType.CHOCOLATE_FOUNTAIN:
      case MachineType.ICE_CREAM_MACHINE:
        animationType = 'pulse';
        break;
      case MachineType.RAINBOW_GENERATOR:
        animationType = 'float';
        speed = 20;
        break;
      default:
        animationType = 'bounce';
    }

    if (animationType === 'rotate') {
      const anim = new Animation(
        'machine-rotate',
        'rotation.y',
        speed,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE
      );
      anim.setKeys([
        { frame: 0, value: 0 },
        { frame: 120, value: Math.PI * 2 },
      ]);
      mesh.animations.push(anim);
      this.scene.beginAnimation(mesh, 0, 120, true);
    } else if (animationType === 'pulse') {
      const anim = new Animation(
        'machine-pulse',
        'scaling',
        speed,
        Animation.ANIMATIONTYPE_VECTOR3,
        Animation.ANIMATIONLOOPMODE_CYCLE
      );
      anim.setKeys([
        { frame: 0, value: mesh.scaling.clone() },
        {
          frame: 30,
          value: new Vector3(
            mesh.scaling.x * 1.05,
            mesh.scaling.y * 1.05,
            mesh.scaling.z * 1.05
          ),
        },
        { frame: 60, value: mesh.scaling.clone() },
      ]);
      mesh.animations.push(anim);
      this.scene.beginAnimation(mesh, 0, 60, true);
    } else if (animationType === 'float') {
      const baseY = mesh.position.y;
      const anim = new Animation(
        'machine-float',
        'position.y',
        speed,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE
      );
      anim.setKeys([
        { frame: 0, value: baseY },
        { frame: 30, value: baseY + 0.2 },
        { frame: 60, value: baseY },
      ]);
      mesh.animations.push(anim);
      this.scene.beginAnimation(mesh, 0, 60, true);
    } else {
      // Bounce
      const baseY = mesh.position.y;
      const anim = new Animation(
        'machine-bounce',
        'position.y',
        speed,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CYCLE
      );
      anim.setKeys([
        { frame: 0, value: baseY },
        { frame: 15, value: baseY + 0.1 },
        { frame: 30, value: baseY },
      ]);
      mesh.animations.push(anim);
      this.scene.beginAnimation(mesh, 0, 30, true);
    }
  }

  public getMachine(id: string): PlacedMachine | undefined {
    return this.machines.get(id);
  }

  public getAllMachines(): PlacedMachine[] {
    return Array.from(this.machines.values());
  }

  public upgradeMachine(id: string): boolean {
    const machine = this.machines.get(id);
    if (!machine) return false;

    machine.level++;
    const scale = 1 + (machine.level - 1) * 0.1;
    machine.mesh.scaling = new Vector3(scale, scale, scale);

    return true;
  }

  public removeMachine(id: string): void {
    const machine = this.machines.get(id);
    if (machine) {
      machine.mesh.dispose();
      if (machine.particles) {
        machine.particles.dispose();
      }
      this.machines.delete(id);
    }
  }

  public dispose(): void {
    this.machines.forEach((machine) => {
      machine.mesh.dispose();
      if (machine.particles) {
        machine.particles.dispose();
      }
    });
    this.machines.clear();
  }
}
